import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ExpandableSection from "@components/animations/ExpandableSection";
import Button from "@components/buttons/Button";
import CardBase from "@components/cards/CardBase";
import cardBaseStyles from "@components/cards/CardBase.module.css";
import styles from "@components/carePlan/ExpertCarePlan.module.css";
import ConditionalTooltip from "@components/ConditionalTooltip";
import Dot from "@components/Dot";
import ExpertAvatar from "@components/ExpertAvatar";
import TaskPopover from "@components/TaskPopover";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import StatusText from "@components/text/StatusText";
import BodyCopy from "@components/typography/BodyCopy";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import { JourneyStatusEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery, GoalStatusEnum, TaskFragment } from "@typing/Generated";
import { colorForStatus, resourceHasChanges, sortByPosition, taskHasChanges } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Task = TaskFragment;

type Props = {
  handleEdit: (task: Task) => void;
  journey: GetJourneyWithPlanQuery["journey"];
  task: Task;
};

const ExpertService = ({ handleEdit, journey, task }: Props) => {
  const myExpert = useMyExpert();
  const intl = useIntl();
  const resourceOpener = useOpener();
  const color = task.archived || !!(task.goal && task.goal.archived) ? "gray-50" : "white";

  const status = useMemo(() => {
    if (!task.completed) {
      return JourneyStatusEnum.IN_PROGRESS;
    } else if (task.goal?.status === GoalStatusEnum.EDIT_MODE || task.goal?.hasDuplicateGoalInEditMode) {
      return JourneyStatusEnum.UPDATING;
    }
    return JourneyStatusEnum.COMPLETED;
  }, [task.completed, task.goal?.hasDuplicateGoalInEditMode, task.goal?.status]);

  const activeGoal = journey.goals?.find(goal => goal.id === task.goal?.activeGoalId);

  const hasEditModeChanges =
    task.goal?.status === GoalStatusEnum.EDIT_MODE &&
    (taskHasChanges(task, activeGoal) || task.resources.some(resource => resourceHasChanges(resource, activeGoal)));

  const cardExtraClassName = hasEditModeChanges ? cardBaseStyles.redBorder : undefined;

  return (
    <CardBase
      body={
        <>
          <div className={styles.task}>
            <div>
              <div className={styles.taskContent}>
                <ExpertAvatar expert={task.expert} />
                <BodyCopy color="gray">{task.title}</BodyCopy>
              </div>
              {myExpert && (
                <div className={styles.taskBottomRow}>
                  <div>
                    <ConditionalTooltip
                      condition={task.resources.length === 0}
                      content={intl.formatMessage({ id: "pages.carePlan.base.expertServices.mustHaveResources" })}
                    >
                      {tooltipTriggerProps => (
                        <div
                          className="flex items-center justify-center gap-sm no-wrap"
                          tabIndex={0}
                          {...tooltipTriggerProps}
                        >
                          <div className={styles.taskPopover}>
                            <Dot color={colorForStatus(status)} unread />
                            <BodyCopy color="gray-500" size="small">
                              <FormattedMessage id={`pages.carePlan.base.expertServices.statuses.${status}`} />
                            </BodyCopy>
                          </div>
                        </div>
                      )}
                    </ConditionalTooltip>
                  </div>
                  <div className="flex items-center gap-md">
                    {task.resources.length > 0 ? (
                      <Button
                        routerLink={journeyRoute({
                          journeyId: journey.id,
                          linkAfterJourney: `/client-visible-tasks/${task.id}`,
                          myExpert
                        })}
                      >
                        <FormattedMessage id="pages.carePlan.base.expertServices.viewResource" />
                      </Button>
                    ) : (
                      <Button
                        fill="clear"
                        routerLink={journeyRoute({
                          journeyId: journey.id,
                          linkAfterJourney: `/client-visible-tasks/${task.id}`,
                          myExpert
                        })}
                      >
                        <IconBase icon="add" slot="start" />
                        <FormattedMessage id="pages.carePlan.base.expertServices.addResources" />
                      </Button>
                    )}
                    {task.resources.length > 0 && (
                      <div
                        aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.toggle" })}
                        className={styles.resourcesToggle}
                        onClick={resourceOpener.toggle}
                      >
                        <Icon icon={resourceOpener.isOpen ? "caretUp" : "caretDown"} size="small" />
                      </div>
                    )}
                  </div>
                  <div></div>
                </div>
              )}
            </div>

            {myExpert && (
              <div className="flex items-center gap-lg">
                <div className="flex flex-column items-center gap-sm">
                  <TaskPopover handleEdit={handleEdit} journey={journey} task={task} />
                  {task.resources.every(resource => resource.ownerViewedAt) && task.resources.length > 0 ? (
                    <StatusText iconOnly status={JourneyStatusEnum.VIEWED} />
                  ) : null}
                </div>
              </div>
            )}
          </div>
          <div>
            <ExpandableSection isOpen={resourceOpener.isOpen}>
              <div className={styles.taskResources}>
                {sortByPosition(task.resources).map(resource => (
                  <div className={styles.taskResource} key={resource.id}>
                    <BodyCopy color="gray">{resource.title}</BodyCopy>
                    {resource.ownerViewedAt ? <StatusText iconOnly status={JourneyStatusEnum.VIEWED} /> : null}
                  </div>
                ))}
              </div>
            </ExpandableSection>
          </div>
        </>
      }
      className={cardExtraClassName}
      color={color}
    />
  );
};

export default ExpertService;
